"use client";

import React from "react";
import { CircleAlert as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ProblemCategory {
  id: string;
  title: string;
  urgency: string;
  description: string;
  symptoms: string;
  actionText: string;
  linkPath: string;
  iconName: string;
}
const problemCategories: ProblemCategory[] = [{
  id: "burst-pipes",
  title: "Burst & Leaking Pipes",
  urgency: "Immediate Hazard",
  description: "Active water line ruptures, freezing pipe fractures, and concealed wall leaks causing structural damage.",
  symptoms: "Pooling water, dropping water pressure, sound of rushing water inside walls.",
  actionText: "Fix Pipe Leaks",
  linkPath: "/problems/",
  iconName: "Droplets"
}, {
  id: "drain-sewer",
  title: "Clogged Drains & Backups",
  urgency: "High Priority",
  description: "Stubborn kitchen sink stoppages, toilet overflows, and main sewer line blockages backing into living areas.",
  symptoms: "Gurgling drains, slow drainage across multiple fixtures, foul sewage odors.",
  actionText: "Clear Blockages",
  linkPath: "/problems/",
  iconName: "Waves"
}, {
  id: "water-heater",
  title: "Water Heater Failures",
  urgency: "Emergency Service",
  description: "Total loss of hot water, leaking tank reservoirs, gas burner errors, and electrical element faults.",
  symptoms: "Cold shower water, discolored rusty water, tank leaking from the base.",
  actionText: "Restore Hot Water",
  linkPath: "/water-heater/",
  iconName: "Flame"
}, {
  id: "sewer-line",
  title: "Sewer Line Emergencies",
  urgency: "Critical Health Risk",
  description: "Tree root intrusions, cracked underground sewer piping, and municipal connection backups in Jonesboro.",
  symptoms: "Yard soggy spots, waste coming up in bathtubs, persistent sewer gas.",
  actionText: "Inspect Main Line",
  linkPath: "/problems/",
  iconName: "ShieldAlert"
}, {
  id: "fixture-toilet",
  title: "Toilet & Fixture Overflow",
  urgency: "Same-Day Fix",
  description: "Continuously running toilets, malfunctioning fill valves, cracked porcelain, and broken shut-off valves.",
  symptoms: "Toilet bowl rising to rim, whistling valves, constant trickling sounds.",
  actionText: "Repair Fixtures",
  linkPath: "/problems/",
  iconName: "Wrench"
}, {
  id: "low-pressure",
  title: "Low Water Pressure Issues",
  urgency: "Standard Diagnostic",
  description: "Sudden drops in building-wide water flow, pressure regulator valve failures, and heavy mineral sediment scale.",
  symptoms: "Trickling faucets, appliances taking too long to fill, fluctuating flow.",
  actionText: "Restore Pressure",
  linkPath: "/problems/",
  iconName: "Pipette"
}];
export default function ProblemCategories() {
  const getIcon = (name: string) => {
    switch (name) {
      case "Droplets":
        return <Droplets className="size-6 text-primary" />;
      case "Waves":
        return <Waves className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-primary" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-primary" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      default:
        return <Pipette className="size-6 text-primary" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header content */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
              <AlertTriangle className="size-3.5 text-accent" />
              <span>Troubleshooting Directory</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore Common Plumbing Problems
            </h2>
            <p className="mt-3 text-muted-foreground text-base md:text-lg">
              Identify your exact issue below for emergency troubleshooting guidance, immediate dispatch, and guaranteed repair solutions in Jonesboro, GA.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" asChild className="border-border hover:bg-muted">
              <Link to="/problems/">
                <span>View All Diagnostics</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button variant="default" asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                <span>24/7 Dispatch</span>
              </a>
            </Button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problemCategories.map((item, index) => <div key={item.id} data-index={index} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="p-3 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center">
                        {getIcon(item.iconName)}
                      </div>
                      <Badge variant={item.urgency.includes("Immediate") || item.urgency.includes("Critical") ? "destructive" : "secondary"} className="text-2xs font-semibold">
                        {item.urgency}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground font-default">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm mt-1">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-4">
                    <div className="rounded-md bg-muted/60 p-3 border border-border/60">
                      <p className="text-xs font-semibold text-foreground mb-1 uppercase tracking-wider">
                        Common Symptoms:
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.symptoms}
                      </p>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6 flex items-center justify-between border-t border-border/60 mt-auto">
                  <span className="text-xs font-medium text-muted-foreground">
                    Jonesboro Emergency Service
                  </span>
                  <Button variant="ghost" size="sm" asChild className="text-primary hover:text-primary hover:bg-secondary font-semibold -mr-2">
                    <Link to={item.linkPath}>
                      <span>{item.actionText}</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        {/* Bottom Fast-Action Callout */}
        <div className="mt-12 rounded-xl bg-secondary border border-border p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg md:text-xl font-bold text-secondary-foreground font-default">
              Cannot find your exact plumbing problem?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak directly with an on-call licensed technician for immediate diagnostic help over the phone.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button variant="outline" asChild className="bg-background border-border hover:bg-muted">
              <Link to="/contact/">
                <span>Request Inspection</span>
              </Link>
            </Button>
            <Button variant="default" asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                <span>Call Jonesboro Plumber</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}